import Debug from 'debug'
import { EventEmitter } from 'events'

import SocketManager from '../socket/SocketManager.js'
import Session from './Session.js'

const debug = Debug('cbio.SessionStream')
const debugStream = Debug('cbio.SessionStream:events')

export default class SessionStream extends EventEmitter {
  #api
  #session
  #sockets = new SocketManager({ getSocketAuth: () => this.#getSocketAuth() })

  #probeInterval

  #metricsInterval

  #lastAlive = {
    rtt: undefined,
    last_alive: undefined
  }

  constructor (api, session) {
    super()
    this.#api = api
    this.#session = session
    debug('constructed SessionStream')

    this.#sockets.on('open', this.#handleOpen)
    this.#sockets.on('error', this.#handleError)
    this.#sockets.on('close', this.#handleClose)
  }

  connect = () => {
    this.#sockets.connect()
    this.#sockets.off('event', this.#handleEvent)
    this.#sockets.on('event', this.#handleEvent)
  }

  disconnect = () => {
    this.#sockets.disconnect()
  }

  sendHighlight = async ({ id, display, target, title, agent } = {}) => {
    this.#sockets.send('highlight', {
      type: 'Highlight',
      id: id || String(Math.random()),
      display: display ?? null,
      timestamp: new Date().toISOString(),
      target,
      title,
      agent
    })
  }

  sendSync = ({ id } = {}) => {
    this.#sockets.send('sync', { type: 'Sync', id: id || String(Math.random()) })
  }

  get metrics () {
    return {
      ...this.#lastAlive,
      latency: this.#sockets.latency
    }
  }

  #handleOpen = () => {
    this.#probeInterval = setInterval(() => {
      this.#sockets.send('probe', { timestamp: Date.now() })
    }, 3_000)

    this.#metricsInterval = setInterval(() => {
      this.emit('metrics.updated', this.metrics)
    }, 1_000)

    this.#lastAlive = {
      ...this.#lastAlive,
      last_alive: new Date()
    }

    this.emit('open')
  }

  #handleEvent = (event, resource) => {
    debugStream(event, resource)
    if (event === 'alive') return this.#onAlive(resource)
    this.emit(event, resource)
  }

  #handleError = (err) => {
    this.emit('error', err)
  }

  #handleClose = () => {
    clearInterval(this.#probeInterval)
    this.#probeInterval = null

    clearInterval(this.#metricsInterval)
    this.#metricsInterval = null

    this.#lastAlive = {
      rtt: undefined,
      last_alive: undefined
    }

    this.emit('close')
  }

  #onAlive = ({ timestamp }) => {
    this.#lastAlive.last_alive = new Date()
    this.#lastAlive.rtt = Date.now() - timestamp
  }

  #getSocketAuth = async () => {
    const tokens = await this.#api.fetch(`${Session.url({ id: this.#session.id })}/tokens`)
    return {
      token: tokens.stream_token,
      url: tokens.stream_url,
      api: this.#api.baseurl
    }
  }
}
